import * as Sentry from '@sentry/vue'
import { createSentryPiniaPlugin } from '@sentry/vue'
import api from '../api/api'

/**
 * Check if current host is localhost
 * @returns {boolean}
 */
export const isLocalhost = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}

export const sentryConfig = {
  colorScheme: 'light',
  showBranding: false,
  triggerLabel: 'Fehler melden',
  formTitle: 'Fehler melden',
  submitButtonLabel: 'Senden',
  cancelButtonLabel: 'Abbrechen',
  confirmButtonLabel: 'Bestätigen',
  addScreenshotButtonLabel: 'Screenshot hinzufügen',
  removeScreenshotButtonLabel: 'Screenshot entfernen',
  nameLabel: 'Name',
  namePlaceholder: 'Dein Name',
  emailLabel: 'E-Mail',
  emailPlaceholder: 'deine.email@beispiel.de',
  isRequiredLabel: 'Pflichtangabe',
  messageLabel: 'Beschreibung',
  messagePlaceholder:
    'Beschreib bitte, welches Fehlverhalten bei dir aufgetreten ist! Was hast du für ein Verhalten erwartet?',
  successMessageText: 'Vielen Dank für dein Feedback.',
  themeLight: {
    foreground: 'var(--quitly-body-color, #4a86e8)',
    background: 'var(--quitly-body-bg, #ffffff)',
    accentForeground: '#ffffff',
    accentBackground: ' #4a86e8',
    successColor: 'var(--quitly-success, #28a745)',
    errorColor: 'var(--quitly-danger, #dc3545)',
    border: '1px solid #4a86e8',
    inputFocusOutline: '2px solid  #4a86e8',
  },
}

/**
 * Check Sentry health endpoint
 * @returns {Promise<boolean>}
 */
export const checkSentryHealth = async () => {
  if (isLocalhost()) {
   return false
  }

  try {
    const result = await api.get('/health/sentry')
    return result.status === 200
  } catch (error) {
    return false
  }
}

/**
 * Initialize Sentry with all configurations
 * @param {Object} app - Vue app instance
 * @param {Object} router - Vue router instance
 * @param {Object} pinia - Pinia instance
 */
export const initializeSentry = (app, router, pinia) => {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENV,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
      }),
      Sentry.feedbackIntegration({
        autoInject: false,
        ...sentryConfig
      }),
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
        },
      }),
    ],
    normalizeDepth: 10,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })

  pinia.use(
    createSentryPiniaPlugin({
      attachPiniaState: true,
      stateTransformer: (state) => {
        if (state.auth?.user) {
          return {
            ...state,
            auth: {
              ...state.auth,
              user: {
                ...state.auth.user,
                password: undefined,
                access_token: undefined,
                refresh_token: undefined,
              },
            },
          }
        }
        return state
      },
    }),
  )
}

/**
 * Set Sentry user information
 * @param {Object} user - User object with firstName, lastName, and email
 */
export const setSentryUser = (user) => {
  if (!isLocalhost() && user) {
    Sentry.setUser({
      username: user.firstName + ' ' + user.lastName,
      email: user.email,
    })
  }
}
